import { ApiError } from "@noa/api-client";
import type {
  ChunkSourceType,
  CorpusFilters,
  CorpusProblemResponse,
  DocumentTypeSchema,
  ValueCount,
} from "@noa/api-core";

/** The filter bar's state, as the inputs hold it. */
export interface CorpusFilterForm {
  documentType?: string;
  sourceSystem?: string;
  /** Schema field values, by field name; only sent with a document type. */
  fields: Record<string, string>;
  name: string;
  /** Local days, "YYYY-MM-DD" as a date input gives them; "" when unset. */
  ingestedFrom: string;
  ingestedTo: string;
}

export const EMPTY_FILTER_FORM: CorpusFilterForm = { fields: {}, name: "", ingestedFrom: "", ingestedTo: "" };

/** The server refuses a name search shorter than this. */
const MIN_NAME_CHARS = 2;

/** Schema field types a text value can be compared to; the server refuses the others. */
const TEXT_FIELD_TYPES = new Set(["string", "enum", "list[string]"]);

/** When a local day, "YYYY-MM-DD" as a date input gives it, starts, `days` later. */
export const parseLocalDay = (day: string, days = 0): Date => {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year, month - 1, date + days);
};

const localDayStart = (day: string, days = 0): string => parseLocalDay(day, days).toISOString();

/** The API filters a filter form stands for; unset inputs are left out. */
export const toCorpusFilters = (form: CorpusFilterForm): CorpusFilters => {
  const filters: CorpusFilters = {};
  if (form.documentType) filters.document_type = form.documentType;
  if (form.sourceSystem) filters.source_system = form.sourceSystem;
  // A schema field belongs to its type's schema: without the type it means nothing
  const fields = Object.fromEntries(Object.entries(form.fields).filter(([, value]) => value));
  if (form.documentType && Object.keys(fields).length > 0) filters.fields = fields;
  const name = form.name.trim();
  if (name.length >= MIN_NAME_CHARS) filters.name_contains = name;
  if (form.ingestedFrom) filters.ingested_from = localDayStart(form.ingestedFrom);
  // The end day is included: the range stops when the next day starts
  if (form.ingestedTo) filters.ingested_to = localDayStart(form.ingestedTo, 1);
  return filters;
};

/** Whether no input holds a value, sent or not (a one-letter search is not sent yet). */
export const isFormEmpty = (form: CorpusFilterForm): boolean =>
  !form.documentType &&
  !form.sourceSystem &&
  Object.values(form.fields).every((value) => !value) &&
  !form.name.trim() &&
  !form.ingestedFrom &&
  !form.ingestedTo;

/** One filter of a selection, as it is sent; `field` names a schema field. */
export type DescribedFilter =
  { kind: "type" | "source" | "name" | "from" | "to"; value: string } | { kind: "field"; field: string; value: string };

/** The filters a selection is made of, in the form's order, leaving out what is not sent. */
export const describeFilters = (form: CorpusFilterForm): DescribedFilter[] => {
  const sent = toCorpusFilters(form);
  const described: DescribedFilter[] = [];
  if (sent.document_type) described.push({ kind: "type", value: sent.document_type });
  if (sent.source_system) described.push({ kind: "source", value: sent.source_system });
  for (const [field, value] of Object.entries(sent.fields ?? {})) described.push({ kind: "field", field, value });
  if (sent.name_contains) described.push({ kind: "name", value: sent.name_contains });
  // Shown as the local days picked, not the instants sent
  if (form.ingestedFrom) described.push({ kind: "from", value: form.ingestedFrom });
  if (form.ingestedTo) described.push({ kind: "to", value: form.ingestedTo });
  return described;
};

/** Whether the filters select part of the corpus rather than all of it. */
export const hasAnyFilter = (filters: CorpusFilters): boolean =>
  Object.entries(filters).some(([key, value]) =>
    key === "fields" ? Object.keys(value ?? {}).length > 0 : value !== undefined && value !== null
  );

/** Why the server refused a corpus request, as its OpenAPI declares it (a 422). */
export type CorpusProblem = CorpusProblemResponse["detail"];

// `satisfies` fails the typecheck when the API declares a code this page does not read
const PROBLEM_CODES = { too_broad: true, too_many: true, invalid_filter: true } satisfies Record<
  CorpusProblem["code"],
  true
>;

const isProblem = (detail: unknown): detail is CorpusProblem =>
  typeof detail === "object" && detail !== null && "code" in detail && String(detail.code) in PROBLEM_CODES;

/** Reads the problem the vectorstore admin API answers in a 422; any other error gives null. */
export const corpusProblem = (error: unknown): CorpusProblem | null =>
  error instanceof ApiError && error.status === 422 && isProblem(error.detail) ? error.detail : null;

/** The fields documents of a type can be filtered on by a text value, sorted, once each. */
export const filterableTextFields = (schemas: DocumentTypeSchema[], documentType: string | undefined): string[] => {
  if (!documentType) return [];
  const names = schemas
    .filter((schema) => schema.doc_type === documentType)
    .flatMap((schema) => schema.fields ?? [])
    // The deprecated "prompt" key still declares a filterable field
    .filter((field) => (field.vectorstore ?? field.prompt)?.filterable)
    .filter((field) => TEXT_FIELD_TYPES.has(field.type ?? "string"))
    .map((field) => field.name);
  return [...new Set(names)].sort();
};

/** A filter's choices: each value with how many documents hold it; a search matches the label alone. */
export const countedOptions = (
  counts: ValueCount[],
  label: (value: string) => string,
  language: string
): { value: string; label: string; searchText: string }[] =>
  counts.map((count) => ({
    value: count.value,
    label: `${label(count.value)} (${count.count.toLocaleString(language)})`,
    searchText: label(count.value),
  }));

/** Where stored documents come from (their `file_type`); an unknown one shows raw. */
export const KNOWN_SOURCES = ["sharepoint", "google_drive", "conformit", "maximo", "blob", "local"] as const;

// Every source type the API declares, so a new one fails the typecheck until it has a label
const CHUNK_SOURCE_TYPES = {
  unstructured: true,
  vision: true,
  llm_description: true,
  fallback: true,
  doclink_image: true,
  doclink_document: true,
} satisfies Record<ChunkSourceType, true>;

/** How a chunk was extracted (its `source_type`); "text" stands for chunks without one. */
export const KNOWN_CHUNK_KINDS = ["text", ...(Object.keys(CHUNK_SOURCE_TYPES) as ChunkSourceType[])];

export const sourceLabelKey = (source: string): string => `vectorstoreAdmin.sources.${source}`;

export const chunkKindKey = (kind: string): string => `vectorstoreAdmin.chunkKinds.${kind}`;
